<template>
  <div
    ref="container"
    class="container"
    :class="{
      dragging: isDragging && !fullSize,
      fullsize: fullSize,
      hidden,
    }"
  >
    <div
      ref="viewer"
      class="viewer"
      :style="style"
      :class="{ fixed: !isDefaultPosition }"
    >
      <ModalViewerHeader ref="header" class="header">
        <div ref="draggable" class="draggable"></div>
        <template #title>
          <ModalViewerHeaderTitle :title="title" />
        </template>

        <template #ui>
          <ModalViewerHeaderUi />
        </template>
        <slot name="header" />
      </ModalViewerHeader>

      <slot class="viewer-content" name="content" />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { onClickOutside, useDraggable, useDebounceFn } from '@vueuse/core';

interface Props {
  title: string;
}

defineProps({
  title: null
});

const emit = defineEmits(["updateHeaderHeight"]);

const route = useRoute();

const fullSizeToggle = useFullSizeToggle();

provide(INJECT_FUILLSIZE_TOGGLE, fullSizeToggle);

const { fullSize } = fullSizeToggle;

const isDefaultPosition = ref<boolean>();

const viewer = ref<HTMLElement | null>(null);

const header = ref<HTMLElement | null>(null);

const draggable = ref<HTMLElement | null>(null);

const hidden = ref<boolean>(false);

const breakpoints = useEvaBreakpoints();

const isAboveL = breakpoints.greaterOrEqual('l');

const {
  position,
  style: dragStyle,
  isDragging,
} = useDraggable(viewer, {
  initialValue: { x: 0, y: 0 },
  handle: draggable,
  pointerTypes: ['mouse'],
  onStart: (p, e) => {
    if (!isAboveL.value) return false;
  },
});

const style = computed(() => {
  if (fullSize.value || !isAboveL.value) return '{ left: 0, top: 0 }';
  return dragStyle.value;
});

const debouncedHidden = useDebounceFn(() => {
  hidden.value = isDragging.value;
}, 500);

watchEffect(() => {
  isDefaultPosition.value = position.value.x === 0 && position.value.y === 0;
});

watchEffect(() => {
  if (isDragging.value) {
    debouncedHidden();
  } else {
    hidden.value = false;
  }
});

onClickOutside(viewer, () => {
  navigateTo(route.path);
});

const { height: headerHeight } = useElementBounding(header);

watchEffect(() => emit('updateHeaderHeight', headerHeight.value));
</script>
<style lang="scss" scoped>
.container {
  display: flex;
  width: 100%;
  overflow: hidden;
  @include above('l') {
    display: flex;
    position: fixed;
    z-index: 100;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    align-items: center;
    justify-content: center;
    overflow-y: hidden;
    background-color: rgb(0 0 0 / 0%);
  }
}
.draggable {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  @include above('l') {
    cursor: grab;
  }
}
.header-navigations {
  overflow: hidden;
}

.viewer {
  display: grid;
  grid-template-rows: auto 1fr;
  width: 100%;
  max-width: 100%;
  height: 100%;
  background: transparent;

  @include above('l') {
    position: relative;
    max-width: 86rem;
    height: 90vh;
    overflow: hidden;
    border: 0.1rem solid var(--color_neutral_200);
    border-radius: 0.8rem;
    box-shadow: rgb(0 0 0 / 56%) 0 22px 70px 4px;
  }
  @include desktop {
    max-width: 120rem;
  }
  &.fixed {
    position: fixed;
    top: 0;
    left: 0;
  }
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: #ababab;
  }

  .viewer-content {
    position: relative;
    width: 100%;
    height: 100%;
  }
}
.dragging {
  .viewer {
    transition: border 10ms $ease-out-quad 200ms;
    border: 0.2rem dashed var(--color_neutral_100);
    &::before {
      background-color: rgb(0 0 0 / 0%);
    }
  }

  &.hidden {
    .viewer-content,
    header {
      visibility: hidden;
    }
  }
}
.fullsize {
  .viewer {
    width: 100%;
    max-width: 100%;
    height: 100%;
    overflow: hidden;
    transition: top 300ms $ease-in-back, left 300ms $ease-in-back,
      height 300ms $ease-in-back, max-width 300ms $ease-in-back;
    border-radius: 0;
  }
}
</style>
